export interface TimeRange {
  startTime: string;
  endTime: string;
}

export interface DayBlock<T extends TimeRange> {
  item: T;
  /** Minutes since midnight. */
  start: number;
  end: number;
  /** Column of the block within its group of overlapping rules. */
  lane: number;
  /** Columns the group of overlapping rules is split into. */
  lanes: number;
}

// Shortest span a rule is drawn with, so a rule whose end is not after its
// start (data that skipped the form) still shows and can be clicked
const MIN_BLOCK_MINUTES = 20;
const MINUTES_PER_DAY = 24 * 60;

/** "HH:mm" to minutes since midnight. */
export function toMinutes(time: string): number {
  const [hours, minutes] = time.split(":");
  return Number.parseInt(hours, 10) * 60 + Number.parseInt(minutes || "0", 10);
}

/** Drawn span of a rule in minutes since midnight. */
function toSpan(range: TimeRange): { start: number; end: number } {
  const start = Math.min(toMinutes(range.startTime), MINUTES_PER_DAY - MIN_BLOCK_MINUTES);
  return { start, end: Math.max(toMinutes(range.endTime), start + MIN_BLOCK_MINUTES) };
}

/**
 * Whole hours the week view shows: from the earliest opening to the latest
 * closing across the week, widened to fit every rule. Without opening hours
 * it shows the whole day.
 */
export function getVisibleHours(
  openingHours: TimeRange[],
  rules: TimeRange[]
): { startHour: number; endHour: number } {
  if (openingHours.length === 0) {
    return { startHour: 0, endHour: 24 };
  }

  // A slot whose closing is not after its opening closes after midnight
  // (18:00-02:00): it keeps the view open to the end of the day, and its
  // hours after midnight (00:00-02:00) fall on the next day's column.
  const openSpans = openingHours.flatMap((slot) => {
    const start = toMinutes(slot.startTime);
    const end = toMinutes(slot.endTime);
    if (end > start) return [{ start, end }];
    return end > 0
      ? [
          { start, end: MINUTES_PER_DAY },
          { start: 0, end },
        ]
      : [{ start, end: MINUTES_PER_DAY }];
  });

  let startHour = 24;
  let endHour = 0;
  for (const { start, end } of [...openSpans, ...rules.map(toSpan)]) {
    startHour = Math.min(startHour, Math.floor(start / 60));
    endHour = Math.max(endHour, Math.ceil(end / 60));
  }
  return { startHour, endHour: Math.min(endHour, 24) };
}

/**
 * Places a day's rules so overlapping ones sit side by side: each group of
 * rules that overlap (directly or through each other) is split into as many
 * lanes as it needs, and each rule takes the first lane free at its start.
 */
export function layoutDay<T extends TimeRange>(items: T[]): DayBlock<T>[] {
  const sorted = items
    .map((item) => ({ item, ...toSpan(item) }))
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const blocks: DayBlock<T>[] = [];
  let group: DayBlock<T>[] = [];
  let laneEnds: number[] = [];
  let groupEnd = Number.NEGATIVE_INFINITY;

  const closeGroup = () => {
    for (const block of group) block.lanes = laneEnds.length;
    blocks.push(...group);
    group = [];
    laneEnds = [];
  };

  for (const range of sorted) {
    if (range.start >= groupEnd) closeGroup();
    let lane = laneEnds.findIndex((laneEnd) => laneEnd <= range.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(range.end);
    } else {
      laneEnds[lane] = range.end;
    }
    group.push({ ...range, lane, lanes: 1 });
    groupEnd = Math.max(groupEnd, range.end);
  }
  closeGroup();

  return blocks;
}
